/* eslint-disable no-unused-vars */
import {
  resolveCurrentWorkerIdByCompany,
  useGetAuth,
  useGetNotiForWorkerCancellations,
  useGetNotiRejections,
  useGetWorkerInvitions,
  useMarkAsReadCancellations,
  useMarkAsReadRejections,
  useNotificationInviteCheck,
  useSubmitWorkerInvitations,
} from "@lhd/hooks";
import { WorkerInvition } from "@lhd/types";
import { getCompanyIdSession } from "@lhd/utility";
import { Form, Radio } from "antd";
import { Fragment, useEffect, useMemo, useState } from "react";
import { RiErrorWarningFill } from "react-icons/ri";
import Button from "../Button";
import { WorkerInviteStyles } from "./styled";
import { isEqual } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_NAME, ROUTES } from "@lhd/constants";
import { TAB_KEYS } from "@lhd/features/Workers/WokerDetail/utility";
import { useSelector } from "react-redux";
import { user } from "@lhd/features/users/selector";

type ModalKey = "invite" | "noti" | null;
enum InvitationStatus {
  REJECTIONS = "rejections",
  CANCELLED_FOR_INVITER = "cancelled_for_inviter",
  CANCELLED_FOR_INVITEE = "cancelled_for_invitee",
  EXPIRED_FOR_INVITER = "expired_for_inviter",
  EXPIRED_FOR_INVITEE = "expired_for_invitee",
}
type Noti = {
  status: InvitationStatus;
  title?: string;
  content?: string;
  users?: string[];
};
const notiMessage: Noti[] = [
  {
    status: InvitationStatus.REJECTIONS,
    title: "招待関連情報",
    content:
      "以下の方に入社招待が拒否されました。\n理由につきましては、直接ご確認ください。",
  },
  {
    status: InvitationStatus.CANCELLED_FOR_INVITER,
    title: "招待の解除",
    content:
      "以下の方への共有が解除されました。\n理由につきましては直接ご確認下さい。",
  },
  {
    status: InvitationStatus.EXPIRED_FOR_INVITER,
    title: "契約満了",
    content: "以下の方は契約満了となりますので、共有が解除されました。",
  },
  {
    status: InvitationStatus.CANCELLED_FOR_INVITEE,
    title: "招待の解除",
    content:
      "以下の会社の共有が解除されました。\n理由につきましては直接ご確認ください。",
  },
  {
    status: InvitationStatus.EXPIRED_FOR_INVITEE,
    title: "契約満了",
    content: "以下の会社にて契約は満了となりますので、共有が解除されました。",
  },
];

const WorkerNotiInvite = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { isUserResigned } = useGetAuth();
  const [form] = Form.useForm();
  const companyId = getCompanyIdSession();
  const [currentModal, setCurrentModal] = useState<ModalKey>(null);
  const [nextModal, setNextModal] = useState<ModalKey>(null);
  const [invites, setInvites] = useState<WorkerInvition[]>([]);
  const [notis, setNotis] = useState<Noti[]>([]);
  const [uuid, setUuid] = useState<string>(() =>
    Math.random().toString(36).substring(2, 15),
  );
  const hasInviteModal = invites.length > 0;
  const hasNotiModal = notis.length > 0;
  const searchParams = new URLSearchParams(location.search);
  const tab = searchParams.get("tab");
  const isCurrentWorkerDetailTab =
    /^\/workers\/\d+$/.test(location.pathname) &&
    tab === TAB_KEYS.EMPLOYEE_SHARE_INFORMATION;
  const auth = useSelector(user);
  const currentWorkerId = resolveCurrentWorkerIdByCompany(auth, companyId);

  const { data: notificationData, isLoading: isNotificationLoading } =
    useNotificationInviteCheck({
      enabled: !!companyId && !isUserResigned,
    });
  const { data: invitationsData } = useGetWorkerInvitions({
    enabled: !!notificationData?.has_invitation,
    uuid,
  });
  const { data: rejectInvitationsData } = useGetNotiRejections({
    enabled: !!notificationData?.has_rejection,
    uuid,
  });
  const { data: cancelledInvitationsData } = useGetNotiForWorkerCancellations({
    enabled: !!notificationData?.has_cancelled,
    uuid,
  });
  const { mutateAsync: markAsReadRejections } = useMarkAsReadRejections();
  const { mutateAsync: markAsReadCancellations } = useMarkAsReadCancellations();
  const { mutateAsync: submitInvitations, isLoading } =
    useSubmitWorkerInvitations();

  const decisions = Form.useWatch("decisions", form);
  const isInviteSubmitDisabled = useMemo(() => {
    if (!invites.length) return true;
    return invites.some((invite, index) => {
      const key = invite.id ?? index;
      return !decisions?.[key];
    });
  }, [invites, decisions]);

  const notiByStatus = useMemo(
    () => Object.fromEntries(notiMessage.map((n) => [n.status, n] as const)),
    [],
  );

  const handleSubmitInvites = async () => {
    try {
      const values = await form.validateFields();
      const inviteActions = invites.map((invite, index) => {
        const key = invite.id ?? index;
        return {
          ...invite,
          is_accepted: values.decisions?.[key] === "approved",
          is_rejected: values.decisions?.[key] === "rejected",
        };
      });
      const hasAccepted = inviteActions?.some((invite) => invite.is_accepted);
      await submitInvitations(
        inviteActions.map((invite) => ({
          id: invite.id,
          is_accepted: invite.is_accepted,
          is_rejected: invite.is_rejected,
        })),
        {
          onSuccess(e) {
            if (isCurrentWorkerDetailTab) {
              const workerId =
                location.pathname.match(/^\/workers\/(\d+)$/)?.[1];
              queryClient.invalidateQueries([
                QUERY_NAME.WORKER_SHARE_SETTINGS,
                workerId,
              ]);
            }
            if (hasAccepted) {
              navigate(
                `/${ROUTES.WORKERS}/${currentWorkerId}?isDisplayInNewTab=true&tab=${TAB_KEYS.EMPLOYEE_SHARE_INFORMATION}`,
              );
            }
          },
        },
      );
      setInvites([]);
      form.resetFields(["decisions"]);
      setCurrentModal(null);
      setNextModal(null);
    } catch (error) {
      console.error("Error submitting invites:", error);
    }
  };

  const handleNotiSubmit = async () => {
    try {
      const rejectionIds =
        rejectInvitationsData?.map((invite) => invite.id) || [];
      const cancellationIds =
        cancelledInvitationsData?.map((invite) => {
          return { id: invite.id, cancellation_type: invite.cancellation_type };
        }) || [];
      if (rejectionIds.length > 0) {
        await markAsReadRejections(rejectionIds);
      }
      if (cancellationIds.length > 0) {
        await markAsReadCancellations(cancellationIds);
      }
      setInvites([]);
      setNotis([]);
      form.resetFields(["decisions"]);
      setCurrentModal(null);
      setNextModal(null);
    } catch (error) {
      console.error("Error marking rejections as read:", error);
    }
  };

  useEffect(() => {
    if (!invitationsData) {
      setInvites([]);
      return;
    }

    setInvites((prevInvites) => {
      const oldInviteIds = prevInvites.map((invite) => invite.id).sort();
      const newInviteIds = invitationsData.map((invite) => invite.id).sort();
      if (isEqual(oldInviteIds, newInviteIds)) return prevInvites;
      return invitationsData;
    });
  }, [invitationsData]);
  useEffect(() => {
    const newNotis: Noti[] = [];

    if (rejectInvitationsData?.length) {
      newNotis.push({
        ...notiByStatus[InvitationStatus.REJECTIONS],
        users: rejectInvitationsData.map((invite) => invite.worker_name),
      });
    }

    if (cancelledInvitationsData?.length) {
      const grouped = cancelledInvitationsData.reduce((acc, invite) => {
        const status = invite.cancellation_type as InvitationStatus;
        if (!acc[status]) acc[status] = [];
        if (
          [
            InvitationStatus.CANCELLED_FOR_INVITER,
            InvitationStatus.EXPIRED_FOR_INVITER,
          ].includes(status)
        ) {
          acc[status].push(invite.worker_name);
        } else {
          acc[status].push(invite.company_name);
        }
        return acc;
      }, {} as Record<InvitationStatus, string[]>);

      (
        [
          InvitationStatus.CANCELLED_FOR_INVITER,
          InvitationStatus.CANCELLED_FOR_INVITEE,
          InvitationStatus.EXPIRED_FOR_INVITER,
          InvitationStatus.EXPIRED_FOR_INVITEE,
        ] as const
      ).forEach((status) => {
        const users = grouped[status];
        if (users?.length) {
          newNotis.push({
            ...notiByStatus[status],
            users,
          });
        }
      });
    }

    setNotis(newNotis);
  }, [rejectInvitationsData, cancelledInvitationsData, notiByStatus]);
  useEffect(() => {
    if (hasInviteModal && hasNotiModal) {
      setCurrentModal("invite");
      setNextModal("noti");
      return;
    }
    if (hasInviteModal) {
      setCurrentModal("invite");
      setNextModal(null);
      return;
    }

    if (hasNotiModal) {
      setCurrentModal("noti");
      setNextModal(null);
      return;
    }

    setCurrentModal(null);
    setNextModal(null);
  }, [hasInviteModal, hasNotiModal]);
  useEffect(() => {
    const id = setInterval(() => {
      setUuid(Math.random().toString(36).substring(2, 15));
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [isNotificationLoading]);

  return (
    <Fragment>
      <WorkerInviteStyles
        open={currentModal === "invite"}
        afterClose={() => {
          if (nextModal) {
            setCurrentModal(nextModal);
            setNextModal(null);
          }
        }}
        centered
        width={420}
        title={
          <div className="d-flex align-item-center gap-1">
            <div className="icon-warning">
              <RiErrorWarningFill />
            </div>
            確認
          </div>
        }
        footer={
          <div className="d-flex justify-center">
            <Button
              className="btn-submit"
              label="確定"
              onClick={handleSubmitInvites}
              disabled={isInviteSubmitDisabled || isLoading}
              loading={isLoading}
            />
          </div>
        }
        maskClosable={false}
        closable={false}
        keyboard={false}
      >
        <p>以下の企業から入社のご招待をいただいております。</p>
        <p>承認または拒否を選択してください。</p>
        <Form
          form={form}
          layout="horizontal"
          className="form-invite"
          initialValues={{
            decisions: Object.fromEntries(
              invites.map((invite, index) => [invite.id ?? index]),
            ),
          }}
        >
          {invites.map((invite, index) => {
            const key = invite.id ?? index;

            return (
              <Form.Item
                key={key}
                label={invite.company_name}
                name={["decisions", key]}
                className="invite-item"
                rules={[
                  {
                    required: true,
                    message: "共有または拒否を選択してください。",
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value="approved" disabled={isLoading}>
                    承認
                  </Radio>
                  <div className="vertical" />
                  <Radio value="rejected" disabled={isLoading}>
                    拒否
                  </Radio>
                </Radio.Group>
              </Form.Item>
            );
          })}
        </Form>
      </WorkerInviteStyles>
      <WorkerInviteStyles
        open={currentModal === "noti"}
        centered
        width={430}
        title={
          <div className="d-flex align-item-center gap-1">
            <div className="icon-warning">
              <RiErrorWarningFill />
            </div>
            招待関連情報
          </div>
        }
        footer={
          <div className="d-flex justify-center">
            <Button
              className="btn-submit"
              label="閉じる"
              onClick={handleNotiSubmit}
            />
          </div>
        }
        maskClosable={false}
        closable={false}
        keyboard={false}
      >
        {notis?.map((noti, index) => (
          <div key={index} className="noti-item">
            <b>{noti.title}</b>
            <p>{noti.content}</p>
            {noti.users && (
              <ul>
                {noti.users.map((user, idx) => (
                  <li key={idx}>{user}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </WorkerInviteStyles>
    </Fragment>
  );
};

export default WorkerNotiInvite;
